/**
 * Crouching — the Spider-Sense Factory's "no" (MVP notes §17, game design spec §16).
 *
 * A crouch is a *held* posture, not a repetition: going down is one event, standing up is another,
 * and staying down is neither. CAM-03 states it plainly ("a held crouch never double-fires"), and
 * the keyboard's crouch key behaves the same way, which is what lets the binary matcher treat both
 * inputs identically.
 *
 * The exit threshold is the entry threshold scaled by the calibrated hysteresis, so hip noise
 * while a child holds a squat cannot produce a stream of stand/crouch pairs.
 *
 * **It never reports standing up because it lost sight of the child.** Retraction on tracking loss
 * belongs to the adapter, exactly as it belongs to the keyboard adapter on blur — a detector that
 * emitted PLAYER_STOOD here would be inventing a movement, and the difference matters because a
 * fabricated posture can answer a question.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../../platform/config/tunables';
import type { DetectorOptions, DetectorOutcome, PoseDetector } from '../detector-types';
import { DEFAULT_POSE_CALIBRATION } from '../pose-calibration';
import { hipMidpoint } from '../pose-geometry';
import { POSE_LANDMARK } from '../pose-types';
import { createTrackingGate } from './tracking-gate';

const CROUCH_LANDMARKS = [
  POSE_LANDMARK.leftHip,
  POSE_LANDMARK.rightHip,
  POSE_LANDMARK.leftKnee,
  POSE_LANDMARK.rightKnee,
];

export function createCrouchDetector(options: DetectorOptions = {}): PoseDetector {
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const calibration = options.calibration ?? DEFAULT_POSE_CALIBRATION;
  const gate = createTrackingGate({ landmarks: CROUCH_LANDMARKS, tunables });

  const enterDrop = calibration.bodyScale * calibration.crouchDropScale;
  const exitDrop = enterDrop * calibration.hysteresisFraction;

  let crouched = false;
  let lastEnteredAt = Number.NEGATIVE_INFINITY;
  /**
   * Neutral re-acquisition (D-068(2)): the next readable frame after a reset or a loss is a
   * baseline. A child who is already down when ZONES becomes BINARY has not just answered "no".
   */
  let awaitingBaseline = false;

  return {
    capability: 'crouch',

    observe(frame): DetectorOutcome {
      const reading = gate.read(frame);
      if (reading.kind === 'blocked') {
        // Forget the posture without a word: the adapter retracts what it has already announced.
        if (reading.lost) {
          crouched = false;
          awaitingBaseline = true;
        }
        return reading.outcome;
      }

      const hips = hipMidpoint(reading.pose);
      if (hips === null) return { kind: 'none' };

      const drop = hips.y - calibration.standingHipY;

      if (awaitingBaseline) {
        awaitingBaseline = false;
        crouched = drop >= enterDrop;
        return { kind: 'none' };
      }

      if (!crouched) {
        if (drop < enterDrop) return { kind: 'none' };
        if (reading.at - lastEnteredAt < tunables.detector.actionDebounceMs)
          return { kind: 'none' };
        crouched = true;
        lastEnteredAt = reading.at;
        return {
          kind: 'event',
          event: { type: 'PLAYER_CROUCHED' },
          confidence: reading.confidence,
        };
      }

      if (drop > exitDrop) return { kind: 'none' };

      crouched = false;
      return {
        kind: 'event',
        event: { type: 'PLAYER_STOOD' },
        confidence: reading.confidence,
      };
    },

    /** A hole in the observation: the posture inside it was not watched (S5-1/S5-3/S5-5). */
    reacquireSilently(): void {
      crouched = false;
      lastEnteredAt = Number.NEGATIVE_INFINITY;
      awaitingBaseline = true;
    },

    reset(): void {
      gate.reset();
      crouched = false;
      lastEnteredAt = Number.NEGATIVE_INFINITY;
      awaitingBaseline = true;
    },
  };
}
